import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Arrow, ImageDefect, ImageScenario } from '../game/imageTypes';
import type { Severity } from '../game/types';
import type { ToolId } from '../game/partners';
import { allTools, toolById } from '../game/partners';
import { LEVELS } from '../scenarios';
import { detectArrows, readPixels } from '../game/detectArrows';
import type { DetectedBlob } from '../game/detectArrows';
import { draftAnswers, draftingAvailable, saveLevel, saveOverride, uploadScreenshot } from '../game/admin';
import { Redactor } from './Redactor';

interface Props {
  /** Editing an existing level, or null to create a new one. */
  existing: ImageScenario | null;
  /**
   * The tool the admin is working in, used as the default for a NEW level.
   *
   * It used to default to 'jira' whatever you were doing, so adding a level
   * while working in another tool silently filed it under Jira unless you
   * noticed the dropdown.
   */
  defaultToolId?: ToolId;
  /**
   * Whether `existing` ships in the bundle.
   *
   * It decides how the edit is stored, and getting it wrong is silent: saving a
   * compiled level through the runtime-level route writes a record that
   * mergeLevels then discards, because the compiled level already owns that id.
   * The edit appears to succeed and changes nothing.
   */
  isCompiled?: boolean;
  onDone: () => void;
  onCancel: () => void;
}

/**
 * One arrow, as the editor holds it while being worked on.
 *
 * `real` and the reasoning live together because they are one decision: an
 * arrow is either a defect with an explanation and a fix, or a decoy with a
 * note saying why it is fine. Neither may be blank — the server refuses both.
 */
interface DraftArrow {
  id: string;
  label: string;
  region: { x: number; y: number; w: number; h: number };
  description: string;
  real: boolean;
  /** Decoy wording. */
  decoyNote: string;
  /** Defect wording. */
  title: string;
  severity: Severity;
  explanation: string;
  fix: string;
  /** Detected but not part of the level. Red UI icons get detected too. */
  keep: boolean;
  /**
   * Filled by the model and not yet confirmed by a person.
   *
   * Tracked per arrow because the verdict is the untrustworthy part: the model
   * is reliable about what an arrow points at and about half right on whether
   * it is a real problem. An unconfirmed arrow is shown as such and the level
   * cannot be saved until every one has been looked at.
   */
  drafted: boolean;
  confidence?: 'high' | 'medium' | 'low';
  pointsAt?: string;
}

const SEVERITIES: Severity[] = ['critical', 'major', 'minor'];

export function LevelEditor({ existing, isCompiled = false, defaultToolId, onDone, onCancel }: Props) {
  const [id, setId] = useState(existing?.id ?? '');
  const [toolId, setToolId] = useState<ToolId>(
    existing?.source.toolId ?? defaultToolId ?? 'jira',
  );
  const [title, setTitle] = useState(existing?.title ?? '');
  const [subtitle, setSubtitle] = useState(existing?.subtitle ?? '');
  const [brief, setBrief] = useState(existing?.brief ?? '');
  const [screen, setScreen] = useState(existing?.source.screen ?? '');
  const [note, setNote] = useState(
    existing?.source.note ?? 'Captured from a ReleaseTEAM test instance, annotated with red arrows.',
  );
  const [alt, setAlt] = useState(existing?.image.alt ?? '');
  const [imageSrc, setImageSrc] = useState(existing?.image.src ?? '');
  const [imageSize, setImageSize] = useState<{ w: number; h: number } | null>(
    existing ? { w: existing.image.width, h: existing.image.height } : null,
  );

  const [arrows, setArrows] = useState<DraftArrow[]>(() =>
    existing ? fromScenario(existing) : [],
  );

  /**
   * A picked file waiting to be cleaned.
   *
   * Nothing is measured or uploaded until it has been through the redactor:
   * the arrows are measured on the CLEANED image so the regions match what
   * players actually see, and the original is never sent anywhere.
   */
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [detectionNote, setDetectionNote] = useState<string | null>(null);
  const [canDraft, setCanDraft] = useState(false);
  const [draftNote, setDraftNote] = useState<string | null>(null);

  useEffect(() => {
    void draftingAvailable().then(setCanDraft);
  }, []);

  const unconfirmed = arrows.filter((a) => a.keep && a.drafted).length;

  const kept = useMemo(() => arrows.filter((a) => a.keep), [arrows]);
  const realCount = kept.filter((a) => a.real).length;

  /**
   * Take a screenshot, measure its arrows, upload it.
   *
   * The measuring happens in the browser against the file the admin picked,
   * using the same thresholds as the build-time Python tool. Regions are never
   * hand-drawn: an eyeballed box drifts from what the arrow actually points at,
   * and the player is the one who finds out.
   */
  const onCleaned = useCallback(
    async (file: Blob) => {
      setPendingFile(null);
      setMessage(null);
      setProblems([]);
      setBusy('Measuring the arrows…');
      try {
        const { data, width, height } = await readPixels(file);
        const result = detectArrows(data, width, height);
        setImageSize({ w: width, h: height });

        setArrows(
          result.blobs.map((blob, index) => draftFromBlob(blob, index)),
        );
        setDetectionNote(
          [
            `${result.blobs.length} candidate${result.blobs.length === 1 ? '' : 's'} found in a ${width}×${height} image.`,
            result.dropped.length
              ? `${result.dropped.length} smaller red blob${result.dropped.length === 1 ? ' was' : 's were'} ignored (largest ${result.dropped[0]} px). A thin arrow can land here — if one is missing, that is where it went.`
              : '',
            'Red UI icons get detected too. Untick anything that is not an arrow.',
          ]
            .filter(Boolean)
            .join(' '),
        );

        setBusy('Uploading the screenshot…');
        const upload = await uploadScreenshot(file, id || 'level');
        if (!upload.ok) {
          setMessage(upload.message);
          setBusy(null);
          return;
        }
        setImageSrc(upload.src ?? '');
        setMessage('Screenshot stored and arrows measured.');
      } catch {
        setMessage('Could not read that image.');
      } finally {
        setBusy(null);
      }
    },
    [id],
  );

  /**
   * Ask the model for a first pass.
   *
   * Everything it returns lands as an unconfirmed draft. Nothing is saved, and
   * the verdicts it chose still have to be agreed with one at a time.
   */
  const draft = useCallback(async () => {
    if (!imageSrc) {
      setMessage('Add a screenshot first.');
      return;
    }
    setDraftNote(null);
    setBusy('Reading the screenshot… this takes a minute or two.');
    const outcome = await draftAnswers(
      imageSrc,
      kept.map((a) => ({ label: a.label, region: a.region })),
      toolById(toolId).name,
    );
    setBusy(null);
    if (!outcome.ok || !outcome.arrows) {
      setMessage(outcome.error ?? 'Could not draft the answers.');
      return;
    }
    const byLabel = new Map(outcome.arrows.map((a) => [a.label, a]));
    setArrows((previous) =>
      previous.map((arrow) => {
        const d = byLabel.get(arrow.label);
        if (!arrow.keep || !d) return arrow;
        return {
          ...arrow,
          description: arrow.description || d.pointsAt,
          real: d.real,
          title: d.title,
          severity: d.severity,
          explanation: d.explanation,
          fix: d.fix,
          decoyNote: d.decoyNote,
          drafted: true,
          confidence: d.confidence,
          pointsAt: d.pointsAt,
        };
      }),
    );
    setDraftNote(
      `Drafted by ${outcome.model ?? 'the model'}. It is reliable about what each ` +
        'arrow points at and roughly half right about whether something is a real ' +
        'problem — confirm every verdict before saving.',
    );
  }, [imageSrc, kept, toolId]);

  const update = useCallback((index: number, patch: Partial<DraftArrow>) => {
    setArrows((previous) =>
      previous.map((arrow, i) => (i === index ? { ...arrow, ...patch } : arrow)),
    );
  }, []);

  /** Agreeing with a drafted arrow is an explicit act, not an absence of one. */
  const confirm = useCallback((index: number) => {
    setArrows((previous) =>
      previous.map((arrow, i) => (i === index ? { ...arrow, drafted: false } : arrow)),
    );
  }, []);

  const onSave = useCallback(async () => {
    setMessage(null);
    setProblems([]);
    if (!imageSrc || !imageSize) {
      setMessage('Add a screenshot first.');
      return;
    }

    /*
     * Derive the id when the field is blank.
     *
     * An empty id used to be sent as-is, producing a PUT to
     * /api/admin/levels/ with nothing after the slash. Express cannot match an
     * empty :id, so it answered 404 — indistinguishable from "you are not an
     * admin", which is what the same routes return to a non-admin. The save
     * looked like a permissions failure and was a blank text box.
     */
    const effectiveId = existing?.id ?? freeId(id || title);
    if (!effectiveId) {
      setMessage('Give the level an id, or a title to derive one from.');
      return;
    }

    const level = toScenario({
      id: effectiveId,
      toolId,
      title,
      subtitle,
      brief,
      screen,
      note,
      alt,
      imageSrc,
      imageSize,
      arrows: kept,
    });
    if (unconfirmed > 0) {
      setMessage(
        `${unconfirmed} drafted answer${unconfirmed === 1 ? '' : 's'} still ` +
          'unconfirmed. Read each one and press Confirm — a drafted verdict is a ' +
          'suggestion, not an answer.',
      );
      return;
    }

    setBusy('Saving…');
    // A compiled level is patched, never replaced: its scenario file stays the
    // record of what was originally written, and the override can be reverted.
    const outcome = isCompiled
      ? await saveOverride(level.id, {
          title: level.title,
          subtitle: level.subtitle,
          brief: level.brief,
          source: level.source,
          image: level.image,
          arrows: level.arrows,
          defects: level.defects,
        })
      : await saveLevel(level);
    setBusy(null);
    setMessage(outcome.message);
    setProblems(outcome.problems);
    if (outcome.ok) onDone();
  }, [id, toolId, title, subtitle, brief, screen, note, alt, imageSrc, imageSize, kept, isCompiled, existing, unconfirmed, onDone]);

  if (pendingFile) {
    return (
      <Redactor
        file={pendingFile}
        onApply={(cleaned) => void onCleaned(cleaned)}
        onCancel={() => setPendingFile(null)}
      />
    );
  }

  return (
    <div className="panel">
      <div className="panel__inner">
        <p className="case-eyebrow">{existing ? 'Edit level' : 'New level'}</p>
        <h1>{existing ? title || existing.title : 'Add a level'}</h1>

        <p className="admin-warning">
          Anything you save here is <b>live to players immediately</b>. There is
          no review step and no draft.
        </p>

        <h2>The screenshot</h2>

        {/*
          Stated here rather than only in the redactor, because this is where
          the choice of screenshot is made. Someone who knows the constraint
          before picking a file can choose one where the sensitive parts and
          the arrows are not on top of each other; the redactor is too late to
          act on it.
        */}
        <p className="admin-note">
          <b>Redaction happens next, and it removes arrows.</b> The arrows are
          measured on the cleaned image, so anything you black out stops being a
          clickable arrow in the level. If a name sits right beside an arrow,
          crop or re-capture the screen rather than covering both — you will be
          told how many arrows a redaction cost before it is applied.
        </p>

        <label className="field">
          <span className="field__label">
            Screenshot (PNG, JPEG or WebP) — you will clean it before it uploads
          </span>
          <input
            className="field__input"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(event) => {
              const picked = event.target.files?.[0];
              if (picked) setPendingFile(picked);
            }}
          />
        </label>
        {detectionNote && <p className="fineprint">{detectionNote}</p>}
        {imageSrc && imageSize && (
          <div className="admin-preview">
            <img src={imageSrc} alt="" />
            {kept.map((arrow) => (
              <span
                key={arrow.id}
                className={`admin-preview__box${arrow.real ? ' admin-preview__box--real' : ''}`}
                style={{
                  left: `${arrow.region.x}%`,
                  top: `${arrow.region.y}%`,
                  width: `${arrow.region.w}%`,
                  height: `${arrow.region.h}%`,
                }}
              >
                {arrow.label}
              </span>
            ))}
          </div>
        )}

        <h2>What it is</h2>
        <label className="field">
          <span className="field__label">Tool</span>
          <select
            className="field__input"
            value={toolId}
            onChange={(event) => setToolId(event.target.value as ToolId)}
          >
            {/* allTools(), not the compiled TOOLS: a tool an admin created at
                runtime has to be selectable, or levels cannot be added to it. */}
            {allTools().map((tool) => (
              <option key={tool.id} value={tool.id}>
                {tool.short} — {toolById(tool.id).name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">Level id (lowercase, no spaces)</span>
          <input
            className="field__input"
            value={id}
            disabled={Boolean(existing)}
            placeholder={title ? slugify(title) : 'jira-sprint-planning'}
            onChange={(event) => setId(event.target.value)}
          />
          {!existing && (
            <span className="fineprint">
              {(() => {
                const wanted = slugify(id || title);
                const actual = freeId(id || title);
                if (!wanted) return 'Leave blank and it is derived from the title.';
                if (wanted !== actual) {
                  return `"${wanted}" belongs to a built-in level, so this will be saved as "${actual}".`;
                }
                return id.trim()
                  ? `Will be saved as "${actual}".`
                  : `Leave blank and it becomes "${actual}".`;
              })()}
            </span>
          )}
          {existing && (
            <span className="fineprint">
              The id cannot change — scores are recorded against it.
            </span>
          )}
        </label>

        <label className="field">
          <span className="field__label">Title</span>
          <input
            className="field__input"
            value={title}
            placeholder="A sprint nobody can finish"
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Subtitle</span>
          <input
            className="field__input"
            value={subtitle}
            placeholder="Active sprint board · JCS"
            onChange={(event) => setSubtitle(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Briefing shown before play</span>
          <textarea
            className="field__input field__input--area"
            value={brief}
            rows={3}
            onChange={(event) => setBrief(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Which screen this is</span>
          <input
            className="field__input"
            value={screen}
            placeholder="Board › Active sprints"
            onChange={(event) => setScreen(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Where the capture came from</span>
          <input
            className="field__input"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">
            Alt text — what a screen reader says instead of the image
          </span>
          <textarea
            className="field__input field__input--area"
            value={alt}
            rows={2}
            onChange={(event) => setAlt(event.target.value)}
          />
        </label>

        <h2>
          The answers — {realCount} real, {kept.length - realCount} decoys
        </h2>

        {canDraft && (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--wide"
              disabled={Boolean(busy) || kept.length === 0 || !imageSrc}
              onClick={() => void draft()}
            >
              Draft the answers from the screenshot
            </button>
            <p className="admin-note">
              <b>A draft is a suggestion, not an answer.</b> Measured against the
              two levels whose answers were confirmed by hand, the model got what
              every arrow points at right — 20 out of 20 — and agreed with the
              author on whether it was a real problem 11 times out of 20. Read
              every verdict and press Confirm. Nothing saves until you have.
            </p>
          </>
        )}
        {draftNote && <p className="admin-note">{draftNote}</p>}
        {unconfirmed > 0 && (
          <p className="form__message" role="status">
            {unconfirmed} drafted answer{unconfirmed === 1 ? '' : 's'} awaiting your
            confirmation.
          </p>
        )}
        <p className="fineprint">
          Every arrow needs a reason. A real one needs what is wrong and how to
          fix it; a decoy needs why it is fine. Explaining why something is
          correct teaches as much as explaining why something is broken.
        </p>

        {arrows.length === 0 && (
          <p className="fineprint">No arrows yet — add a screenshot above.</p>
        )}

        {arrows.map((arrow, index) => (
          <div
            key={arrow.id}
            className={`admin-arrow${arrow.keep ? '' : ' admin-arrow--dropped'}${
              arrow.drafted ? ' admin-arrow--drafted' : ''
            }`}
          >
            <div className="admin-arrow__head">
              <span className="admin-arrow__label">{arrow.label}</span>
              <label className="admin-arrow__toggle">
                <input
                  type="checkbox"
                  checked={arrow.keep}
                  onChange={(event) => update(index, { keep: event.target.checked })}
                />
                Is an arrow
              </label>
              {arrow.keep && (
                <label className="admin-arrow__toggle">
                  <input
                    type="checkbox"
                    checked={arrow.real}
                    onChange={(event) => update(index, { real: event.target.checked })}
                  />
                  Real mistake
                </label>
              )}
              <span className="fineprint">
                {arrow.region.x.toFixed(1)}%, {arrow.region.y.toFixed(1)}%
              </span>
              {arrow.drafted && (
                <>
                  <span className="admin-tag admin-tag--draft">
                    drafted · {arrow.confidence ?? 'low'} confidence
                  </span>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => confirm(index)}
                  >
                    Confirm
                  </button>
                </>
              )}
            </div>
            {arrow.drafted && arrow.pointsAt && (
              <p className="fineprint">
                <b>Model read this as:</b> {arrow.pointsAt}
              </p>
            )}

            {arrow.keep && (
              <>
                <label className="field">
                  <span className="field__label">What it points at (for screen readers)</span>
                  <input
                    className="field__input"
                    value={arrow.description}
                    placeholder="Arrow pointing at the 100 point estimate"
                    onChange={(event) => update(index, { description: event.target.value })}
                  />
                </label>

                {arrow.real ? (
                  <>
                    <label className="field">
                      <span className="field__label">What is wrong</span>
                      <input
                        className="field__input"
                        value={arrow.title}
                        placeholder="A 100 point story inside a sprint"
                        onChange={(event) => update(index, { title: event.target.value })}
                      />
                    </label>
                    <label className="field">
                      <span className="field__label">How bad</span>
                      <select
                        className="field__input"
                        value={arrow.severity}
                        onChange={(event) =>
                          update(index, { severity: event.target.value as Severity })
                        }
                      >
                        {SEVERITIES.map((severity) => (
                          <option key={severity} value={severity}>
                            {severity}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span className="field__label">Why it is wrong</span>
                      <textarea
                        className="field__input field__input--area"
                        rows={3}
                        value={arrow.explanation}
                        onChange={(event) => update(index, { explanation: event.target.value })}
                      />
                    </label>
                    <label className="field">
                      <span className="field__label">How to fix it</span>
                      <textarea
                        className="field__input field__input--area"
                        rows={2}
                        value={arrow.fix}
                        onChange={(event) => update(index, { fix: event.target.value })}
                      />
                    </label>
                  </>
                ) : (
                  <label className="field">
                    <span className="field__label">Why this one is fine</span>
                    <textarea
                      className="field__input field__input--area"
                      rows={3}
                      value={arrow.decoyNote}
                      onChange={(event) => update(index, { decoyNote: event.target.value })}
                    />
                  </label>
                )}
              </>
            )}
          </div>
        ))}

        {problems.length > 0 && (
          <div className="form__message" role="alert">
            <b>Not saved:</b>
            <ul className="admin-problems">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </div>
        )}
        {message && problems.length === 0 && (
          <p className="form__message" role="status">
            {message}
          </p>
        )}

        <div className="sheet__actions" style={{ marginTop: 20 }}>
          <button type="button" className="btn" disabled={Boolean(busy)} onClick={() => void onSave()}>
            {busy ?? 'Save — goes live now'}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** Ids and filenames are [a-z0-9-]; the server slugifies again on arrival. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/**
 * An id no compiled level already owns.
 *
 * A new level that takes a built-in level's id saves fine and is then never
 * shown, because the merge gives the compiled level precedence. The server
 * refuses the collision now; this stops the admin running into it at all.
 */
function freeId(base: string): string {
  const taken = new Set(LEVELS.map((level) => level.id));
  const root = slugify(base);
  if (!root || !taken.has(root)) return root;
  for (let n = 2; n < 50; n += 1) {
    const candidate = `${root}-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${root}-${Date.now()}`;
}

function draftFromBlob(blob: DetectedBlob, index: number): DraftArrow {
  return {
    id: `arrow-${index + 1}`,
    label: String(index + 1),
    region: blob.region,
    description: '',
    real: false,
    decoyNote: '',
    title: '',
    severity: 'major',
    explanation: '',
    fix: '',
    keep: true,
    drafted: false,
  };
}

function fromScenario(level: ImageScenario): DraftArrow[] {
  const byArrow = new Map(level.defects.map((defect) => [defect.arrowId, defect]));
  return level.arrows.map((arrow) => {
    const defect = byArrow.get(arrow.id);
    return {
      id: arrow.id,
      label: arrow.label,
      region: arrow.region,
      description: arrow.description,
      real: Boolean(defect),
      decoyNote: arrow.decoyNote ?? '',
      title: defect?.title ?? '',
      severity: defect?.severity ?? 'major',
      explanation: defect?.explanation ?? '',
      fix: defect?.fix ?? '',
      keep: true,
      // Existing content was written by a person; it is not awaiting review.
      drafted: false,
    };
  });
}

function toScenario(input: {
  id: string;
  toolId: ToolId;
  title: string;
  subtitle: string;
  brief: string;
  screen: string;
  note: string;
  alt: string;
  imageSrc: string;
  imageSize: { w: number; h: number };
  arrows: DraftArrow[];
}): ImageScenario {
  const arrows: Arrow[] = input.arrows.map((arrow) => ({
    id: arrow.id,
    label: arrow.label,
    region: arrow.region,
    description: arrow.description.trim(),
    ...(arrow.real ? {} : { decoyNote: arrow.decoyNote.trim() }),
  }));

  const defects: ImageDefect[] = input.arrows
    .filter((arrow) => arrow.real)
    .map((arrow) => ({
      id: `${arrow.id}-defect`,
      arrowId: arrow.id,
      title: arrow.title.trim(),
      severity: arrow.severity,
      explanation: arrow.explanation.trim(),
      fix: arrow.fix.trim(),
    }));

  return {
    id: input.id.trim(),
    title: input.title.trim(),
    subtitle: input.subtitle.trim(),
    brief: input.brief.trim(),
    source: {
      toolId: input.toolId,
      screen: input.screen.trim(),
      kind: 'screenshot',
      note: input.note.trim(),
    },
    image: {
      src: input.imageSrc,
      width: input.imageSize.w,
      height: input.imageSize.h,
      alt: input.alt.trim(),
    },
    arrows,
    defects,
  };
}
